import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import { ManualLoader, Wrapper } from "src/components";
import { ContentContainer, PageHeader } from "src/components/Layouts/AppShell";
import {
  RebindLeaseOption,
  useGetRebindLeasesQuery,
  useRebindLeaseMutation,
} from "src/store/services/privateApi";

export const CURRENT_REBIND_STATUSES = new Set([
  "Current",
  "Pending Renewal",
  "Under Eviction",
  "Notice to Vacate",
  "Month to Month",
  "Pending",
]);

/** Home offers a connect button only when there is one live lease to bind to. */
export function singleHomeRebindOffer(leases?: RebindLeaseOption[]) {
  const live = (leases ?? []).filter(
    (lease) => !lease.current && CURRENT_REBIND_STATUSES.has(lease.status)
  );
  return live.length === 1 ? live[0] : undefined;
}

function leasePlace(lease: RebindLeaseOption) {
  const unit = lease.unit ? `unit ${lease.unit}` : "this lease";
  return lease.propertyName ? `${unit} at ${lease.propertyName}` : unit;
}

export function RebindConfirm({
  lease,
  current,
  onCancel,
}: {
  lease: RebindLeaseOption;
  current?: RebindLeaseOption | null;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [rebindLease, { isLoading }] = useRebindLeaseMutation();
  const [error, setError] = useState("");

  const connect = async () => {
    setError("");
    try {
      await rebindLease({ targetPersonId: lease.personId }).unwrap();
      onCancel();
    } catch {
      setError(
        t("home.rebind.error", "That lease could not be switched. Try again.")
      );
    }
  };

  const isConnect = !current;

  return (
    <Dialog
      open
      fullWidth
      maxWidth="xs"
      onClose={isLoading ? undefined : onCancel}
    >
      <DialogTitle>
        {isConnect
          ? t("home.rebind.titleConnect", "Connect a lease")
          : t("home.rebind.title", "Switch lease")}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
          {current
            ? t(
                "home.rebind.confirmSwitch",
                "Switch from {{from}} ({{fromStatus}}) to {{to}} ({{toStatus}})? You can switch back. Payment methods stay on the lease where you saved them.",
                {
                  from: leasePlace(current),
                  fromStatus: current.status,
                  to: leasePlace(lease),
                  toStatus: lease.status,
                }
              )
            : t(
                "home.rebind.confirmConnect",
                "Connect your account to {{to}} ({{toStatus}})? You can switch later. Payment methods stay on the lease where you saved them.",
                { to: leasePlace(lease), toStatus: lease.status }
              )}
        </Typography>
        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" disabled={isLoading} onClick={onCancel}>
          {t("home.rebind.cancel", "Cancel")}
        </Button>
        <Button variant="contained" disabled={isLoading} onClick={connect}>
          {isConnect
            ? t("home.rebind.connect", "Connect")
            : t("home.rebind.switch", "Switch")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const SettingsLeasesScreen = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetRebindLeasesQuery();
  const [pending, setPending] = useState<RebindLeaseOption | null>(null);
  const leases = data?.leases ?? [];
  const current = leases.find((lease) => lease.current) ?? data?.current;
  const others = leases.filter((lease) => !lease.current);

  const isConnect = !current;

  return (
    <Wrapper isAuthorizedWrapper>
      <ContentContainer size="narrow">
        <PageHeader
          title={
            isConnect
              ? t("home.rebind.titleConnect", "Connect a lease")
              : t("home.rebind.title", "Switch lease")
          }
        />
        {isLoading && <ManualLoader />}
        {!isLoading && (
          <Box sx={{ px: 1 }}>
            <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
              {isConnect
                ? t(
                    "home.rebind.introConnect",
                    "This login is not on a lease yet. Connect it to a lease under your name to pay or view that account."
                  )
                : t(
                    "home.rebind.intro",
                    "Your account shows one lease at a time. Switch to another lease under your name when you need to pay or view it. You can switch back. Payment methods stay on the lease where you saved them."
                  )}
            </Typography>
            {isError && (
              <Typography variant="body2" color="error">
                {t(
                  "home.rebind.loadError",
                  "Leases could not be loaded. Try again."
                )}
              </Typography>
            )}
            {current && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("home.rebind.connectedNow", "You're on")}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {current.propertyName}
                </Typography>
                <Typography variant="body2">
                  {t("home.rebind.unitStatus", "Unit {{unit}} · {{status}}", {
                    unit: current.unit,
                    status: current.status,
                  })}
                </Typography>
              </Box>
            )}
            {others.length === 0 && !isError && (
              <Typography variant="body2" color="text.secondary">
                {isConnect
                  ? t(
                      "home.rebind.noneConnect",
                      "No lease under your name is available to connect yet."
                    )
                  : t(
                      "home.rebind.none",
                      "You don't have another lease under your name."
                    )}
              </Typography>
            )}
            {others.map((lease) => (
              <Box
                key={lease.personId}
                sx={{
                  borderTop: "1px solid",
                  borderColor: "divider",
                  py: 1.5,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {lease.propertyName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("home.rebind.unitStatus", "Unit {{unit}} · {{status}}", {
                    unit: lease.unit,
                    status: lease.status,
                  })}
                </Typography>
                <Button
                  sx={{ mt: 1 }}
                  variant="outlined"
                  onClick={() => setPending(lease)}
                >
                  {isConnect
                    ? t("home.rebind.connectTo", "Connect to this lease")
                    : t("home.rebind.switchTo", "Switch to this lease")}
                </Button>
                {pending?.personId === lease.personId && (
                  <RebindConfirm
                    lease={lease}
                    current={current}
                    onCancel={() => setPending(null)}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}
      </ContentContainer>
    </Wrapper>
  );
};
